import { type } from 'arktype';
import { rruleSchema } from '@zag/common/validity';

// ---------------------------------------------------------------------------
// Shared validators (story 23-9 BE-3)
// ---------------------------------------------------------------------------

/**
 * Arktype validator for an ISO 8601 calendar date string in the form `YYYY-MM-DD`.
 * Use in place of `type('string')` for any field that must hold a calendar date.
 * Rejects malformed input ("", " ", "2026-13-99", "x") at the API boundary
 * before it reaches `new Date(...)` and produces an `Invalid Date`.
 */
export const isoDateString = type(/^\d{4}-\d{2}-\d{2}$/);

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export const labourPoolClaimStatuses = ['pending_approval', 'approved', 'rejected', 'cancelled', 'withdrawn'] as const;
export type LabourPoolClaimStatus = (typeof labourPoolClaimStatuses)[number];

// ---------------------------------------------------------------------------
// Domain type — returned by the API
// ---------------------------------------------------------------------------

export interface LabourPoolClaim {
  id: string;
  offerId: string;
  /** ID of the employee being claimed (from the offer) */
  employeeId: string;
  employeeName: string;
  requestId: string | null;
  claimingTeamId: string;
  claimingTeamName: string;
  claimedBy: string;
  claimedByName: string;
  targetPlanInstanceId: string | null;
  startDate: string;
  endDate: string;
  rrule: string | null;
  status: LabourPoolClaimStatus;
  reason: string | null;
  reviewedBy: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Create schema — POST /api/v1/labour-pool/claims
// ---------------------------------------------------------------------------

export const createLabourPoolClaimSchema = type({
  offerId: type('string').configure({
    description: 'ID of the Labour Pool offer being claimed.',
    examples: ['offer_abc123'],
  }),
  claimingTeamId: type('string').configure({
    description: 'ID of the team claiming the offered employee.',
    examples: ['team_abc123'],
  }),
  'requestId?': type('string | null').configure({
    description: 'Optional ID of the labour-pool request this claim fulfils. `null` clears any linkage.',
    examples: ['request_abc123'],
  }),
  'targetPlanInstanceId?': type('string | null').configure({
    description: 'Optional plan instance the claim is attached to for scheduling. `null` clears the linkage.',
    examples: ['planinst_abc123'],
  }),
  startDate: isoDateString.configure({
    description: 'Inclusive start date of the claim as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-20'],
  }),
  endDate: isoDateString.configure({
    description: 'Inclusive end date of the claim as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-26'],
  }),
  'rrule?': rruleSchema.configure({
    description: 'Optional iCal RRULE body refining which dates the claim is active.',
    examples: ['FREQ=WEEKLY;BYDAY=MO,WE'],
  }),
  'reason?': type('string | null').configure({
    description: 'Optional rationale shown to the offering team during approval. `null` clears the reason.',
    examples: ['Covering sick leave for this week.'],
  }),
}).configure({ description: 'Payload for creating a Labour Pool claim against an active offer.' });

export type CreateLabourPoolClaim = typeof createLabourPoolClaimSchema.infer;

// ---------------------------------------------------------------------------
// Query schema — GET /api/v1/labour-pool/claims
// ---------------------------------------------------------------------------

export const labourPoolClaimQuerySchema = type({
  /** Filter by the claiming team (own team's outgoing claims) */
  'claimingTeamId?': 'string',
  /** Filter by offer's team (incoming claims on your offers) */
  'offeringTeamId?': 'string',
  /** Filter by the specific offer — used to fetch incoming claims on an own offer card. */
  'offerId?': 'string',
  /** Filter by the linked labour-pool request — used to fetch all claims covering a single need (story 23-12). */
  'requestId?': 'string',
  'status?': type.enumerated(...labourPoolClaimStatuses),
});

export type LabourPoolClaimQuery = typeof labourPoolClaimQuerySchema.infer;

/**
 * Body schema for `POST /labour-pool/claims/:id/recalls` (story 23-12).
 * Optional `reason` is persisted to `reviewNotes`.
 */
export const recallClaimSchema = type({
  'reason?': type('string').configure({
    description: 'Optional planner-supplied reason for the recall. Persisted to reviewNotes.',
    examples: ['Employee called in sick'],
  }),
});

export type RecallClaim = typeof recallClaimSchema.infer;

// ---------------------------------------------------------------------------
// Review schema — validated on PUT /api/v1/labour-pool/claims/:id/approve|reject
// ---------------------------------------------------------------------------

export const labourPoolClaimReviewSchema = type({
  'reviewNotes?': type('string | null').configure({
    description: 'Optional reviewer note recorded on approval or rejection. `null` clears the note.',
    examples: ['Approved — employee fully qualified.'],
  }),
}).configure({ description: 'Payload submitted when approving or rejecting a Labour Pool claim.' });

export type LabourPoolClaimReview = typeof labourPoolClaimReviewSchema.infer;

// ---------------------------------------------------------------------------
// Direct floater assignment (simplified flow — skips offer/claim/approve cycle)
// ---------------------------------------------------------------------------

export const assignFloaterEntrySchema = type({
  employeeId: type('string').configure({
    description: 'Employee ID for this slice.',
    examples: ['employee_abc123'],
  }),
  startDate: isoDateString.configure({
    description: 'Inclusive start of this slice as ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-20'],
  }),
  endDate: isoDateString.configure({
    description: 'Inclusive end of this slice as ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-26'],
  }),
  'rrule?': rruleSchema.configure({
    description: 'Optional iCal RRULE body refining which dates this assignment slice is active.',
    examples: ['FREQ=WEEKLY;BYDAY=MO,WE'],
  }),
}).configure({ description: 'Per-employee assignment slice (story 23-8).' });

export type AssignFloaterEntry = typeof assignFloaterEntrySchema.infer;

/**
 * Maximum entries per bulk assign request (story 23-9 BE-6). Prevents DoS / pool
 * exhaustion via huge arrays. Hard cap; not configurable per tenant.
 */
export const MAX_ASSIGN_BATCH_SIZE = 50;

export const assignFloaterSchema = type({
  'employeeIds?': type('string[]')
    .atMostLength(MAX_ASSIGN_BATCH_SIZE)
    .configure({
      description: `IDs of employees to assign as floaters in a single batch (legacy path — all share the top-level startDate/endDate). Mutually exclusive with \`assignments\`. Maximum ${MAX_ASSIGN_BATCH_SIZE} entries.`,
      examples: [['employee_abc123', 'employee_def456']],
    }),
  'assignments?': assignFloaterEntrySchema
    .array()
    .atMostLength(MAX_ASSIGN_BATCH_SIZE)
    .configure({
      description: `Per-employee assignment slices (story 23-8). When set, top-level employeeIds/startDate/endDate are ignored. Used by the temporal-split direct-assign path. Maximum ${MAX_ASSIGN_BATCH_SIZE} entries.`,
    }),
  fromTeamId: type('string').configure({
    description: 'ID of the team lending the employees (their home team).',
    examples: ['team_abc123'],
  }),
  toTeamId: type('string').configure({
    description: "ID of the team receiving the floaters (the need's team).",
    examples: ['team_def456'],
  }),
  'startDate?': isoDateString.configure({
    description: 'Inclusive start (legacy path). Required when `employeeIds` is used; ignored when `assignments` is used.',
    examples: ['2026-04-20'],
  }),
  'endDate?': isoDateString.configure({
    description: 'Inclusive end (legacy path). Required when `employeeIds` is used; ignored when `assignments` is used.',
    examples: ['2026-04-26'],
  }),
  'rrule?': rruleSchema.configure({
    description: 'Optional iCal RRULE body shared by legacy employee assignments; ignored when `assignments` is used.',
    examples: ['FREQ=WEEKLY;BYDAY=MO,WE'],
  }),
  'requestId?': type('string | null').configure({
    description: 'Optional labour-pool request this assignment fulfils. `null` clears the linkage.',
    examples: ['request_abc123'],
  }),
  'notes?': type('string | null').configure({
    description: 'Optional free-form planner notes. `null` clears the note.',
    examples: ['Direct assignment agreed between planners.'],
  }),
}).configure({ description: 'Payload for directly assigning floaters without going through the offer/claim cycle.' });

export type AssignFloater = typeof assignFloaterSchema.infer;

export interface AssignFloaterResultEntry {
  employeeId: string;
  status: 'created' | 'rejected';
  error?: string;
}

export interface AssignFloaterMultiResult {
  results: AssignFloaterResultEntry[];
}

// ---------------------------------------------------------------------------
// Matching needs query — GET /api/v1/labour-pool/requests/matching
// ---------------------------------------------------------------------------

export const matchingNeedsQuerySchema = type({
  /** Start of the offer date range */
  fromDate: 'string',
  /** End of the offer date range */
  toDate: 'string',
  /** Optional: only return needs for this claiming team */
  'requestingTeamId?': 'string',
  /** Optional skill IDs the offer can satisfy */
  'skillIds?': 'string',
});

export type MatchingNeedsQuery = typeof matchingNeedsQuerySchema.infer;
